import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import { Layout } from './components/Layout';
import { SignupForms } from './pages/SignupForms';
import { SignupFormEditor } from './pages/SignupFormEditor';
import { EmbedCodes } from './pages/EmbedCodes';
import { EmailTemplates } from './pages/EmailTemplates';
import { EmailTemplateEditor } from './pages/EmailTemplateEditor';
import { FormConfiguration } from './pages/FormConfiguration';
import { ConfigurationsList } from './pages/ConfigurationsList';
import { Submissions } from './pages/Submissions';
import { Subscriptions } from './pages/Subscriptions';
import { Contacts } from './pages/Contacts';
import { Users } from './pages/Users';
import { Settings } from './pages/Settings';
import { AccountSettings } from './pages/AccountSettings';
import { SuperAdminSettings } from './pages/SuperAdminSettings';
import { Newsletters } from './pages/Newsletters';
import { NewsletterEditor } from './pages/NewsletterEditor';
import { NewsletterDetail } from './pages/NewsletterDetail';
import { Campaigns } from './pages/Campaigns';
import { CampaignBuilder } from './pages/CampaignBuilder';
import { CampaignDetail } from './pages/CampaignDetail';
import { AudienceBuilder } from './pages/AudienceBuilder';
import { AudienceEditor } from './pages/AudienceEditor';
import { AuditLog } from './pages/AuditLog';
import { Tags } from './pages/Tags';
import { TagDetail } from './pages/TagDetail';
import { Categories } from './pages/Categories';
import { Regions } from './pages/Regions';
import { MailingLists } from './pages/MailingLists';
import { MailingListDetail } from './pages/MailingListDetail';
import { RolesPermissions } from './pages/RolesPermissions';
import { WorkspaceProvider, useWorkspace } from './contexts/WorkspaceContext';
import { SendBucketManager } from './pages/SendBucketManager';
import { ApiTest } from './pages/ApiTest';
import { GptApiTest } from './pages/GptApiTest';
import { ErrorLog } from './pages/ErrorLog';
import { ConfirmSubscription } from './pages/ConfirmSubscription';
import { Onboarding } from './pages/Onboarding';
import { Login } from './pages/Login';
import { Dashboard } from './pages/Dashboard';
import { Loader2, AlertTriangle, RefreshCw } from 'lucide-react';
import { FormTemplatesLibrary } from './pages/FormTemplatesLibrary';
import { FormTemplateLibraryEditor } from './pages/FormTemplateLibraryEditor';
import { EmailTemplatesLibrary } from './pages/EmailTemplatesLibrary';
import { EmailTemplateLibraryEditor } from './pages/EmailTemplateLibraryEditor';
import { BrazilContentReference } from './pages/BrazilContentReference';
import { LandingPages } from './pages/LandingPages';
import { LandingPageEditor } from './pages/LandingPageEditor';
import { PublicLandingPage } from './pages/PublicLandingPage';
import { PublicFormEmbed } from './pages/PublicFormEmbed';
import { MediaLibrary } from './pages/MediaLibrary';
function AppGate() {
  const {
    appState,
    error
  } = useWorkspace();
  // Loading state
  if (appState === 'loading') {
    return <div className="min-h-screen bg-gray-50 flex items-center justify-center" data-id="element-0">
        <div className="text-center" data-id="element-1">
          <Loader2 className="h-10 w-10 text-blue-500 animate-spin mx-auto mb-4" data-id="element-2" />
          <p className="text-sm text-gray-500 font-medium" data-id="element-3">
            Loading workspace...
          </p>
        </div>
      </div>;
  }
  // Error state
  if (appState === 'error') {
    return <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4" data-id="element-4">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-10 text-center max-w-md w-full" data-id="element-5">
          <div className="mx-auto w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mb-6" data-id="element-6">
            <AlertTriangle className="h-8 w-8 text-red-500" data-id="element-7" />
          </div>
          <h1 className="text-xl font-semibold text-gray-900 mb-2" data-id="element-8">
            Connection Error
          </h1>
          <p className="text-gray-500 text-sm mb-2" data-id="element-9">
            Failed to connect to the backend. Please check your connection and
            try again.
          </p>
          {error && <p className="text-xs text-red-500 bg-red-50 rounded-lg p-3 mb-6 font-mono break-all" data-id="element-10">
              {error}
            </p>}
          <button onClick={() => window.location.reload()} className="inline-flex items-center gap-2 px-4 py-2 bg-gray-900 text-white rounded-lg text-sm font-medium hover:bg-gray-800 transition-colors" data-id="element-11">
            <RefreshCw className="h-4 w-4" data-id="element-12" />
            Retry
          </button>
        </div>
      </div>;
  }
  // Onboarding — no users exist yet
  if (appState === 'onboarding') {
    return <Onboarding data-id="element-13" />;
  }
  // Login — users exist but no session
  if (appState === 'login') {
    return <Login data-id="element-14" />;
  }
  // Ready — full admin app
  return <Layout data-id="element-15">
      <Routes data-id="element-16">
        <Route path="/" element={<Navigate to="/dashboard" replace data-id="element-18" />} data-id="element-17" />
        <Route path="/dashboard" element={<Dashboard data-id="element-20" />} data-id="element-19" />

        <Route path="/mailing-lists" element={<MailingLists data-id="element-22" />} data-id="element-21" />
        <Route path="/mailing-lists/:listId" element={<MailingListDetail data-id="element-24" />} data-id="element-23" />

        <Route path="/newsletters" element={<Newsletters data-id="element-26" />} data-id="element-25" />
        <Route path="/newsletters/new" element={<NewsletterEditor data-id="element-28" />} data-id="element-27" />
        <Route path="/newsletters/:id" element={<NewsletterDetail data-id="element-30" />} data-id="element-29" />
        <Route path="/newsletters/:id/edit" element={<NewsletterEditor data-id="element-32" />} data-id="element-31" />

        <Route path="/contacts" element={<Contacts data-id="element-34" />} data-id="element-33" />

        <Route path="/forms" element={<SignupForms data-id="element-36" />} data-id="element-35" />
        <Route path="/forms/new" element={<SignupFormEditor data-id="element-38" />} data-id="element-37" />
        <Route path="/forms/:id" element={<SignupFormEditor data-id="element-40" />} data-id="element-39" />
        <Route path="/forms/:id/embed" element={<EmbedCodes data-id="element-42" />} data-id="element-41" />

        <Route path="/templates" element={<EmailTemplates data-id="element-44" />} data-id="element-43" />
        <Route path="/templates/new" element={<EmailTemplateEditor data-id="element-46" />} data-id="element-45" />
        <Route path="/templates/:id" element={<EmailTemplateEditor data-id="element-48" />} data-id="element-47" />

        <Route path="/form-templates" element={<FormTemplatesLibrary data-id="element-50" />} data-id="element-49" />
        <Route path="/form-templates/new" element={<FormTemplateLibraryEditor data-id="element-52" />} data-id="element-51" />
        <Route path="/form-templates/:id" element={<FormTemplateLibraryEditor data-id="element-54" />} data-id="element-53" />

        <Route path="/email-templates-library" element={<EmailTemplatesLibrary data-id="element-56" />} data-id="element-55" />
        <Route path="/email-templates-library/new" element={<EmailTemplateLibraryEditor data-id="element-58" />} data-id="element-57" />
        <Route path="/email-templates-library/:id" element={<EmailTemplateLibraryEditor data-id="element-60" />} data-id="element-59" />

        <Route path="/configurations" element={<ConfigurationsList data-id="element-62" />} data-id="element-61" />
        <Route path="/configuration/new" element={<FormConfiguration data-id="element-64" />} data-id="element-63" />
        <Route path="/configuration/:id" element={<FormConfiguration data-id="element-66" />} data-id="element-65" />

        <Route path="/tags" element={<Tags data-id="element-68" />} data-id="element-67" />
        <Route path="/tags/:tagName" element={<TagDetail data-id="element-70" />} data-id="element-69" />

        <Route path="/categories" element={<Categories data-id="element-72" />} data-id="element-71" />

        <Route path="/regions" element={<Regions data-id="element-74" />} data-id="element-73" />

        <Route path="/campaigns" element={<Campaigns data-id="element-76" />} data-id="element-75" />
        <Route path="/campaigns/new" element={<CampaignBuilder data-id="element-78" />} data-id="element-77" />
        <Route path="/campaigns/:id" element={<CampaignDetail data-id="element-80" />} data-id="element-79" />

        <Route path="/audiences" element={<AudienceBuilder data-id="element-82" />} data-id="element-81" />
        <Route path="/audiences/new" element={<AudienceEditor data-id="element-84" />} data-id="element-83" />
        <Route path="/audiences/:id" element={<AudienceEditor data-id="element-86" />} data-id="element-85" />

        <Route path="/submissions" element={<Submissions data-id="element-88" />} data-id="element-87" />
        <Route path="/subscriptions" element={<Subscriptions data-id="element-90" />} data-id="element-89" />
        <Route path="/audit-log" element={<AuditLog data-id="element-92" />} data-id="element-91" />
        <Route path="/users" element={<Users data-id="element-94" />} data-id="element-93" />
        <Route path="/roles" element={<RolesPermissions data-id="element-96" />} data-id="element-95" />
        <Route path="/settings" element={<Settings data-id="element-98" />} data-id="element-97" />
        <Route path="/account" element={<AccountSettings data-id="element-100" />} data-id="element-99" />

        <Route path="/send-buckets" element={<SendBucketManager data-id="element-102" />} data-id="element-101" />

        <Route path="/api-test" element={<ApiTest data-id="element-104" />} data-id="element-103" />
        <Route path="/api-test/gpt" element={<GptApiTest data-id="element-106" />} data-id="element-105" />

        <Route path="/error-log" element={<ErrorLog data-id="element-108" />} data-id="element-107" />

        <Route path="/brazil-content" element={<BrazilContentReference data-id="element-110" />} data-id="element-109" />

        <Route path="/landing-pages" element={<LandingPages data-id="element-112" />} data-id="element-111" />
        <Route path="/landing-pages/new" element={<LandingPageEditor data-id="element-114" />} data-id="element-113" />
        <Route path="/landing-pages/:id" element={<LandingPageEditor data-id="element-116" />} data-id="element-115" />

        <Route path="/media-library" element={<MediaLibrary data-id="element-118" />} data-id="element-117" />

        <Route path="/super-admin" element={<SuperAdminSettings data-id="element-120" />} data-id="element-119" />
      </Routes>
    </Layout>;
}
export function App() {
  return <WorkspaceProvider data-id="element-121">
      <Toaster position="top-right" richColors closeButton data-id="element-122" />
      <Router data-id="element-123">
        <Routes data-id="element-124">
          {/* Public routes — no auth needed */}
          <Route path="/confirm" element={<ConfirmSubscription data-id="element-126" />} data-id="element-125" />
          <Route path="/page/:slug" element={<PublicLandingPage data-id="element-128" />} data-id="element-127" />
          <Route path="/embed/:slug" element={<PublicFormEmbed data-id="element-130" />} data-id="element-129" />

          {/* All other routes go through the auth/onboarding gate */}
          <Route path="*" element={<AppGate data-id="element-132" />} data-id="element-131" />
        </Routes>
      </Router>
    </WorkspaceProvider>;
}